import { useState, useEffect, useCallback } from 'react';
import { expertWalletApi } from '../../../api/paymentApis/expertWalletApis/expertWallet.api'; // Adjust path
import { toast } from 'sonner';

export interface WalletOverviewData {
  walletBalance: number;
  allTimeEarnings: number;
  totalRedeemed: number;
  isVerified: boolean;
  isPayoutFrozen: boolean;
  thisMonthEarnings: number;
  monthlyBreakdown: number[]; // Array of 12 numbers representing Jan-Dec
}

export const useExpertWallet = () => {
  const [walletData, setWalletData] = useState<WalletOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWalletOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await expertWalletApi.getWalletOverview();
      
      if (response.success && response.data) {
        setWalletData(response.data);
      } else {
        throw new Error(response.message || 'Failed to parse wallet ledger metrics');
      }
    } catch (err: any) {
      console.error('Fetch Expert Wallet Overview Error:', err);
      
      // Auto-logout handling context for 403 authorization rejections
      if (err.response?.status === 403) {
        toast.error(err.response?.data?.message || 'Access denied. Account status restricted.');
        
        localStorage.removeItem('expertCode');
        localStorage.removeItem('expert_code');
        localStorage.removeItem('expertName');
        
        setTimeout(() => {
          window.location.href = '/expert-login';
        }, 2000);
        return;
      }

      const errorMessage = err.response?.data?.message || 'Failed to load wallet metrics summary';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWalletOverview();
  }, [fetchWalletOverview]);

  return {
    loading,
    error,
    refetch: fetchWalletOverview,
    
    // Explicit UI state mappings to safely support your ExpertWalletCheckout.tsx dependencies
    walletBalance: walletData?.walletBalance || 0,
    allTimeEarnings: walletData?.allTimeEarnings || 0,
    totalRedeemed: walletData?.totalRedeemed || 0,
    thisMonthEarnings: walletData?.thisMonthEarnings || 0,
    monthlyBreakdown: walletData?.monthlyBreakdown || new Array(12).fill(0),
    isVerified: walletData?.isVerified || false,
    isPayoutFrozen: walletData?.isPayoutFrozen || false
  };
};